import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
 Box,
 Button,
 Flex,
 Heading,
 IconButton,
 Input,
 InputGroup,
 InputLeftElement,
 Select,
 Spacer,
 Spinner,
 Text,
 Textarea,
 Tooltip,
 useToast,
} from '@chakra-ui/react';
import {
 FiArrowLeft,
 FiChevronDown,
 FiChevronUp,
 FiClipboard,
 FiColumns,
 FiEdit3,
 FiList,
 FiPaperclip,
 FiPlus,
 FiSearch,
 FiX,
} from 'react-icons/fi';
import { MainContent } from '../../components/Layout';
import {
 getSchedule,
 updateSchedule,
 updateActivity,
 deleteActivity,
} from '../../services/scheduleService';
import {
 BLUE,
 ORANGE,
 ACTIVITY_COLUMNS,
 ACTIVITY_STATUS_LABEL,
 countOverdue,
 daysUntil,
 formatDate,
 progressBreakdown,
 statusMeta,
} from './constants';
import {
 Avatar,
 AvatarStack,
 DueChip,
 FaseTag,
 SegmentedProgress,
 StatusPill,
} from './components';
import ActivityModal from './ActivityModal';
import { useScheduleSocket } from './useScheduleSocket';
import { AttentionModal } from '../../components/Modals';

export default function ScheduleDetail() {
 const { id } = useParams();
 const navigate = useNavigate();
 const toast = useToast();

 const [schedule, setSchedule] = useState(null);
 const [loading, setLoading] = useState(true);
 const [observations, setObservations] = useState('');
 const [savingObs, setSavingObs] = useState(false);
 const [obsOpen, setObsOpen] = useState(false);
 const [modalOpen, setModalOpen] = useState(false);
 const [editingActivity, setEditingActivity] = useState(null);
 const [presetStatus, setPresetStatus] = useState(null);
 const [activityToDelete, setActivityToDelete] = useState(null);
 const [deletingActivity, setDeletingActivity] = useState(false);

 // Redesign: visão em quadro/lista + filtros do board.
 const [view, setView] = useState('quadro');
 const [search, setSearch] = useState('');
 const [filterResp, setFilterResp] = useState('');
 const [filterFase, setFilterFase] = useState('');

 const [draggingId, setDraggingId] = useState(null);
 const [dragOver, setDragOver] = useState(null);

 // silent=true recarrega sem trocar a tela pelo spinner (evita o "pulo pro
 // topo" quando o realtime/foco atualiza o board).
 async function load(silent = false) {
  if (!silent) setLoading(true);
  try {
   const data = await getSchedule(id);
   setSchedule(data);
   setObservations((prev) => (prev ? prev : data?.client_observations || ''));
  } catch (err) {
   toast({ title: 'Erro ao carregar cronograma', status: 'error' });
  } finally {
   if (!silent) setLoading(false);
  }
 }

 useEffect(() => {
  load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [id]);

 // Recarrega ao voltar o foco à aba — reflete mudanças feitas em outro lugar
 // sem precisar recarregar a página manualmente.
 useEffect(() => {
  const refetch = () => {
   if (document.visibilityState === 'visible') load(true);
  };
  document.addEventListener('visibilitychange', refetch);
  window.addEventListener('focus', refetch);
  return () => {
   document.removeEventListener('visibilitychange', refetch);
   window.removeEventListener('focus', refetch);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [id]);

 // Realtime: recarrega o board quando qualquer usuário altera este cronograma.
 useScheduleSocket(id, () => load(true));

 const activities = useMemo(() => schedule?.activities || [], [schedule]);

 // Opções dos filtros derivadas de TODAS as atividades.
 const responsibleOptions = useMemo(() => {
  const map = new Map();
  activities.forEach((a) => {
   if (a.responsible) map.set(a.responsible.id_user, a.responsible.username);
  });
  return Array.from(map, ([id_user, username]) => ({ id_user, username }));
 }, [activities]);

 const faseOptions = useMemo(() => {
  const set = new Set();
  activities.forEach((a) => a.fase && set.add(a.fase));
  return Array.from(set).sort();
 }, [activities]);

 const filtered = useMemo(() => {
  const term = search.trim().toLowerCase();
  return activities.filter((a) => {
   if (term) {
    const hay = `${a.name} ${a.description || ''}`.toLowerCase();
    if (!hay.includes(term)) return false;
   }
   if (filterResp && String(a.id_responsible) !== filterResp) return false;
   if (filterFase && a.fase !== filterFase) return false;
   return true;
  });
 }, [activities, search, filterResp, filterFase]);

 const byStatus = useMemo(() => {
  const groups = Object.fromEntries(ACTIVITY_COLUMNS.map((s) => [s, []]));
  filtered.forEach((a) => {
   (groups[a.status] || groups.NAO_INICIADO).push(a);
  });
  return groups;
 }, [filtered]);

 // Stats/progresso do cabeçalho refletem o cronograma inteiro (não os filtros).
 const breakdown = progressBreakdown(activities);
 const overdue = countOverdue(activities);
 const remaining = daysUntil(schedule?.end_date);
 const hasFilters = !!(search || filterResp || filterFase);

 function remainingLabel(n) {
  if (n == null) return null;
  if (n > 0) return `${n} dias restantes`;
  if (n === 0) return 'vence hoje';
  return `atrasado há ${-n} dias`;
 }

 async function saveObservations() {
  setSavingObs(true);
  try {
   await updateSchedule(schedule.id_schedule, { client_observations: observations });
   toast({ title: 'Observações salvas', status: 'success' });
  } catch (err) {
   toast({ title: 'Erro ao salvar', status: 'error' });
  } finally {
   setSavingObs(false);
  }
 }

 function openCreate(status) {
  setEditingActivity(null);
  setPresetStatus(status || null);
  setModalOpen(true);
 }

 function openEdit(activity) {
  setEditingActivity(activity);
  setPresetStatus(null);
  setModalOpen(true);
 }

 async function confirmDeleteActivity() {
  if (activityToDelete == null) return;
  setDeletingActivity(true);
  try {
   await deleteActivity(activityToDelete);
   toast({ title: 'Atividade removida', status: 'success' });
   setActivityToDelete(null);
   load(true);
  } catch (err) {
   toast({ title: 'Erro ao remover atividade', status: 'error' });
  } finally {
   setDeletingActivity(false);
  }
 }

 async function handleDrop(status) {
  const card = activities.find((a) => a.id_activity === draggingId);
  setDragOver(null);
  setDraggingId(null);
  if (!card || card.status === status) return;

  setSchedule((prev) => ({
   ...prev,
   activities: prev.activities.map((a) =>
    a.id_activity === card.id_activity ? { ...a, status } : a,
   ),
  }));
  try {
   await updateActivity(card.id_activity, { status });
  } catch (err) {
   toast({ title: 'Não foi possível mover a atividade', status: 'error' });
   load(true);
  }
 }

 if (loading) {
  return (
   <MainContent>
    <Flex justify='center' py='60px'>
     <Spinner size='lg' color={BLUE} thickness='3px' />
    </Flex>
   </MainContent>
  );
 }
 if (!schedule) return null;

 return (
  <MainContent>
   {/* Breadcrumb */}
   <Flex align='center' gap='6px' fontSize='13px' color='gray.400' mb='12px'>
    <Flex
     as='button'
     align='center'
     gap='5px'
     color='gray.600'
     _hover={{ color: 'gray.800' }}
     onClick={() => navigate('/cronogramas')}
    >
     <FiArrowLeft size={14} /> Cronogramas
    </Flex>
    <Text>/</Text>
    <Text color='gray.700' fontWeight='600' noOfLines={1}>
     {schedule.name}
    </Text>
   </Flex>

   {/* CABEÇALHO DO CRONOGRAMA */}
   <Box bg='#fff' border='1px solid #E7E9EE' borderRadius='14px' p='22px 24px' boxShadow='0 1px 2px rgba(16,32,48,0.04)'>
    <Flex justify='space-between' align='flex-start' gap='24px' flexWrap='wrap'>
     <Box minW={0}>
      <Flex align='center' gap='10px' flexWrap='wrap'>
       <Heading size='lg' color='gray.800' letterSpacing='-0.01em' noOfLines={1}>
        {schedule.name}
       </Heading>
       <StatusPill status={schedule.status} size='lg' />
      </Flex>
      <Flex align='center' gap='8px' mt='8px' fontSize='13px' color='gray.500' flexWrap='wrap'>
       <Text>{schedule.company?.fantasyName || schedule.company?.name}</Text>
       <Text color='gray.300'>•</Text>
       <Text>{`${formatDate(schedule.start_date)} → ${formatDate(schedule.end_date)}`}</Text>
       {remainingLabel(remaining) && (
        <>
         <Text color='gray.300'>•</Text>
         <Text color={remaining != null && remaining < 0 ? 'red.500' : 'gray.500'} fontWeight={remaining != null && remaining < 0 ? '700' : '400'}>
          {remainingLabel(remaining)}
         </Text>
        </>
       )}
      </Flex>
     </Box>
     <Flex align='center' gap='16px'>
      <AvatarStack people={schedule.consultants || []} size={32} />
      <Button variant='primary' onClick={() => openCreate()} leftIcon={<FiPlus />}>
       Nova atividade
      </Button>
     </Flex>
    </Flex>

    {/* stats + progresso segmentado */}
    <Flex align='center' gap='28px' mt='20px' flexWrap='wrap'>
     <Flex gap='28px'>
      <StatBig value={breakdown.total} label='Atividades' />
      <StatBig value={breakdown.done} label='Concluídas' color='#00A15C' />
      <StatBig value={overdue} label='Atrasadas' color={overdue > 0 ? '#D9534F' : undefined} />
     </Flex>
     <Box flex='1' minW='240px'>
      <Flex justify='space-between' fontSize='12px' color='gray.500' mb='6px'>
       <Text>Progresso geral</Text>
       <Text fontWeight='800' color='gray.800'>{breakdown.pct}%</Text>
      </Flex>
      <SegmentedProgress done={breakdown.done} doing={breakdown.doing} blocked={breakdown.blocked} total={breakdown.total} />
     </Box>
    </Flex>

    {/* observações do cliente (recolhível, editável) */}
    <Box borderTop='1px solid #EEF1F4' mt='20px' pt='12px'>
     <Flex
      as='button'
      align='center'
      gap='8px'
      w='100%'
      onClick={() => setObsOpen((o) => !o)}
      fontSize='12px'
      fontWeight='800'
      letterSpacing='0.06em'
      textTransform='uppercase'
      color='gray.500'
     >
      <Box as={FiEdit3} color={BLUE} />
      Observações do cliente
      {!obsOpen && (
       <Text as='span' fontWeight='400' letterSpacing='0' textTransform='none' color='gray.400' noOfLines={1}>
        {observations ? observations : '— clique para expandir'}
       </Text>
      )}
      <Spacer />
      <Box as={obsOpen ? FiChevronUp : FiChevronDown} color='gray.400' />
     </Flex>
     {obsOpen && (
      <Box mt='12px'>
       <Textarea
        bg='#FAFBFC'
        size='sm'
        minH='88px'
        placeholder='Registre observações e combinados com o cliente…'
        value={observations}
        onChange={(e) => setObservations(e.target.value)}
       />
       <Flex justify='flex-end' mt='8px'>
        <Button size='sm' variant='primary' onClick={saveObservations} isLoading={savingObs}>
         Salvar observações
        </Button>
       </Flex>
      </Box>
     )}
    </Box>
   </Box>

   {/* TOOLBAR: toggle de visão + filtros */}
   <Flex align='center' justify='space-between' gap='16px' mt='20px' mb='14px' flexWrap='wrap'>
    <Flex bg='#EAEDF1' borderRadius='10px' p='3px' gap='2px'>
     <ViewTab active={view === 'quadro'} onClick={() => setView('quadro')} icon={<FiColumns size={14} />} label='Quadro' />
     <ViewTab active={view === 'lista'} onClick={() => setView('lista')} icon={<FiList size={14} />} label='Lista' />
    </Flex>
    <Flex align='center' gap='10px' flexWrap='wrap'>
     <InputGroup size='sm' w='220px'>
      <InputLeftElement pointerEvents='none' color='gray.400'>
       <FiSearch size={14} />
      </InputLeftElement>
      <Input
       borderRadius='10px'
       bg='#fff'
       placeholder='Buscar atividade…'
       value={search}
       onChange={(e) => setSearch(e.target.value)}
      />
     </InputGroup>
     {responsibleOptions.length > 0 && (
      <Select
       size='sm'
       w='170px'
       borderRadius='10px'
       bg='#fff'
       value={filterResp}
       onChange={(e) => setFilterResp(e.target.value)}
       placeholder='Responsável'
      >
       {responsibleOptions.map((r) => (
        <option key={r.id_user} value={r.id_user}>{r.username}</option>
       ))}
      </Select>
     )}
     {faseOptions.length > 0 && (
      <Select
       size='sm'
       w='160px'
       borderRadius='10px'
       bg='#fff'
       value={filterFase}
       onChange={(e) => setFilterFase(e.target.value)}
       placeholder='Fase'
      >
       {faseOptions.map((f) => (
        <option key={f} value={f}>{f}</option>
       ))}
      </Select>
     )}
    </Flex>
   </Flex>

   {activities.length === 0 ? (
    <Flex direction='column' align='center' justify='center' py='46px' color='gray.400' bg='#fff' border='1px solid #E7E9EE' borderRadius='14px'>
     <FiClipboard size={28} />
     <Text mt='8px' fontSize='sm'>Nenhuma atividade cadastrada.</Text>
     <Button mt='12px' variant='primary' size='sm' leftIcon={<FiPlus />} onClick={() => openCreate()}>
      Criar primeira atividade
     </Button>
    </Flex>
   ) : view === 'lista' ? (
    <ActivityListView rows={filtered} onRowClick={openEdit} emptyFiltered={hasFilters && filtered.length === 0} />
   ) : (
    <Flex gap='10px' overflowX='auto' pb='10px' align='flex-start'>
     {ACTIVITY_COLUMNS.map((status) => (
      <BoardColumn
       key={status}
       status={status}
       cards={byStatus[status]}
       isOver={dragOver === status}
       onDragOver={(e) => {
        e.preventDefault();
        if (dragOver !== status) setDragOver(status);
       }}
       onDragLeave={() => setDragOver((s) => (s === status ? null : s))}
       onDrop={(e) => {
        e.preventDefault();
        handleDrop(status);
       }}
       onAdd={() => openCreate(status)}
       onCardClick={openEdit}
       onCardDelete={(idA) => setActivityToDelete(idA)}
       draggingId={draggingId}
       setDraggingId={setDraggingId}
      />
     ))}
    </Flex>
   )}

   <ActivityModal
    isOpen={modalOpen}
    onClose={() => setModalOpen(false)}
    idSchedule={schedule.id_schedule}
    idCompany={schedule.id_company}
    activity={editingActivity}
    presetStatus={presetStatus}
    existingPhases={faseOptions}
    onSaved={() => {
     setModalOpen(false);
     load(true);
    }}
   />

   <AttentionModal
    isOpen={activityToDelete != null}
    onClose={() => setActivityToDelete(null)}
    submit={confirmDeleteActivity}
    isLoading={deletingActivity}
    redType
    title='Remover atividade?'
   />
  </MainContent>
 );
}

function StatBig({ value, label, color }) {
 return (
  <Box>
   <Text fontSize='20px' fontWeight='800' color={color || 'gray.800'} lineHeight='1'>
    {value}
   </Text>
   <Text fontSize='11px' color='gray.400' fontWeight='600' letterSpacing='0.06em' mt='3px' textTransform='uppercase'>
    {label}
   </Text>
  </Box>
 );
}

function ViewTab({ active, onClick, icon, label }) {
 return (
  <Flex
   as='button'
   align='center'
   gap='6px'
   onClick={onClick}
   px='14px'
   py='6px'
   borderRadius='8px'
   fontSize='13px'
   fontWeight='600'
   bg={active ? '#fff' : 'transparent'}
   color={active ? 'gray.800' : 'gray.500'}
   boxShadow={active ? '0 1px 3px rgba(16,32,48,0.12)' : 'none'}
  >
   {icon}
   {label}
  </Flex>
 );
}

function ActivityListView({ rows, onRowClick, emptyFiltered }) {
 const cols = '1fr 150px 150px 130px 70px';
 const headers = ['Atividade', 'Fase', 'Status', 'Prazo', 'Resp.'];
 return (
  <Box bg='#fff' border='1px solid #E7E9EE' borderRadius='14px' overflow='hidden'>
   <Box display='grid' gridTemplateColumns={cols} gap='12px' px='20px' py='12px' borderBottom='1px solid #EEF1F4'>
    {headers.map((h) => (
     <Text key={h} fontSize='11px' fontWeight='800' letterSpacing='0.08em' color='gray.400' textTransform='uppercase'>
      {h}
     </Text>
    ))}
   </Box>
   {emptyFiltered ? (
    <Flex justify='center' py='28px' color='gray.400' fontSize='sm'>
     Nenhuma atividade encontrada
    </Flex>
   ) : (
    rows.map((a) => {
     const meta = statusMeta(a.status);
     return (
      <Box
       key={a.id_activity}
       display='grid'
       gridTemplateColumns={cols}
       gap='12px'
       alignItems='center'
       px='20px'
       py='13px'
       borderBottom='1px solid #F2F4F7'
       fontSize='13px'
       cursor='pointer'
       _hover={{ bg: '#FAFBFC' }}
       onClick={() => onRowClick(a)}
      >
       <Text fontWeight='600' color='gray.800' noOfLines={1}>{a.name}</Text>
       <Box>{a.fase ? <FaseTag fase={a.fase} /> : <Text color='gray.300'>—</Text>}</Box>
       <Flex align='center' gap='6px' color='gray.600'>
        <Box w='7px' h='7px' borderRadius='999px' bg={meta.solid} />
        <Text fontSize='12px' noOfLines={1}>{ACTIVITY_STATUS_LABEL[a.status] || a.status}</Text>
       </Flex>
       <Box><DueChip endDate={a.end_date} status={a.status} /></Box>
       <Box>
        {a.responsible ? (
         <Avatar name={a.responsible.username} size={24} />
        ) : (
         <Box w='24px' h='24px' borderRadius='999px' border='2px dashed #CBD2DC' />
        )}
       </Box>
      </Box>
     );
    })
   )}
  </Box>
 );
}

function BoardColumn({
 status,
 cards,
 isOver,
 onDragOver,
 onDragLeave,
 onDrop,
 onAdd,
 onCardClick,
 onCardDelete,
 draggingId,
 setDraggingId,
}) {
 const meta = statusMeta(status);
 return (
  <Box
   minW='272px'
   maxW='288px'
   flex='0 0 auto'
   bg='#EEF1F4'
   borderRadius='14px'
   p='6px'
   transition='background .15s ease, box-shadow .15s ease'
   boxShadow={isOver ? `inset 0 0 0 2px ${meta.solid}` : 'none'}
   onDragOver={onDragOver}
   onDragLeave={onDragLeave}
   onDrop={onDrop}
  >
   {/* Cabeçalho da coluna (sobre o fundo da coluna, sem faixa colorida) */}
   <Flex align='center' gap='8px' pt='10px' pr='10px' pb='8px' pl='12px'>
    <Box w='8px' h='8px' borderRadius='999px' bg={meta.solid} flexShrink={0} />
    <Text fontWeight='700' fontSize='12px' color='#4A5560' textTransform='uppercase' letterSpacing='0.04em' noOfLines={1}>
     {meta.label}
    </Text>
    <Box bg='#fff' color='#6B7682' fontSize='11px' fontWeight='700' borderRadius='999px' px='8px' minW='20px' textAlign='center'>
     {cards.length}
    </Box>
    <Spacer />
    <Tooltip label='Adicionar atividade' hasArrow>
     <IconButton
      aria-label='Adicionar'
      size='xs'
      variant='ghost'
      minW='22px'
      h='22px'
      color={ORANGE}
      _hover={{ bg: 'blackAlpha.100' }}
      icon={<FiPlus />}
      onClick={onAdd}
     />
    </Tooltip>
   </Flex>

   <Box minH='36px' px='2px'>
    {cards.length === 0 && (
     <Flex
      align='center'
      justify='center'
      py='16px'
      mb='6px'
      borderRadius='8px'
      border='1px dashed #CFD5DE'
      color='gray.400'
      fontSize='11px'
      textAlign='center'
     >
      Arraste atividades para cá
     </Flex>
    )}
    {cards.map((a) => (
     <ActivityCard
      key={a.id_activity}
      activity={a}
      isDragging={draggingId === a.id_activity}
      onDragStart={() => setDraggingId(a.id_activity)}
      onDragEnd={() => setDraggingId(null)}
      onClick={() => onCardClick(a)}
      onDelete={() => onCardDelete(a.id_activity)}
     />
    ))}
   </Box>

   <Button
    w='100%'
    size='xs'
    variant='ghost'
    color='gray.500'
    fontWeight='700'
    justifyContent='flex-start'
    leftIcon={<FiPlus />}
    onClick={onAdd}
    _hover={{ bg: 'blackAlpha.50' }}
   >
    Adicionar
   </Button>
  </Box>
 );
}

function ActivityCard({ activity, isDragging, onDragStart, onDragEnd, onClick, onDelete }) {
 const attachments = activity.attachments?.length || 0;
 return (
  <Box
   draggable
   onDragStart={(e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(activity.id_activity));
    onDragStart();
   }}
   onDragEnd={onDragEnd}
   onClick={onClick}
   cursor='grab'
   bg='#fff'
   borderRadius='12px'
   p='12px 14px'
   mb='8px'
   border='1px solid #E6EAEE'
   boxShadow='0 1px 2px rgba(16,32,48,0.05)'
   opacity={isDragging ? 0.4 : 1}
   transition='box-shadow .15s ease, border-color .15s ease'
   role='group'
   _hover={{ boxShadow: '0 3px 10px rgba(16,32,48,0.08)', borderColor: '#C8D2DC' }}
   _active={{ cursor: 'grabbing' }}
  >
   {activity.fase && (
    <Box mb='8px'>
     <FaseTag fase={activity.fase} />
    </Box>
   )}
   <Flex justify='space-between' align='flex-start' gap='6px'>
    <Text fontWeight='500' fontSize='14px' lineHeight='1.35' noOfLines={2} color='gray.800'>
     {activity.name}
    </Text>
    <Box
     as='button'
     onClick={(e) => {
      e.stopPropagation();
      onDelete();
     }}
     color='gray.300'
     opacity={0}
     _groupHover={{ opacity: 1 }}
     _hover={{ color: 'red.400' }}
     title='Remover'
     mt='1px'
    >
     <FiX size={13} />
    </Box>
   </Flex>

   {activity.description && (
    <Text fontSize='11px' color='gray.500' noOfLines={1} mt='3px'>
     {activity.description}
    </Text>
   )}

   <Flex align='center' justify='space-between' mt='8px'>
    <DueChip endDate={activity.end_date} status={activity.status} />
    <Flex align='center' gap='6px' minW={0}>
     {attachments > 0 && (
      <Flex align='center' gap='2px' fontSize='11px' color='gray.500'>
       <FiPaperclip size={11} />
       {attachments}
      </Flex>
     )}
     {activity.responsible ? (
      <Avatar name={activity.responsible.username} size={22} />
     ) : (
      <Box w='22px' h='22px' borderRadius='999px' border='2px dashed #CBD2DC' />
     )}
    </Flex>
   </Flex>
  </Box>
 );
}
